import React, { useState, useRef, useEffect } from "react";
import "@/App.css";
import { 
  ChevronDown, 
  Play, 
  Pause, 
  Volume2, 
  ExternalLink, 
  Clock, 
  Leaf, 
  Zap,
  ChevronLeft,
  ChevronRight,
  MapPin,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselPrevious, 
  CarouselNext 
} from "@/components/ui/carousel";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

// Past section carousel data with images and descriptions
const pastCarouselData = [
  { image: "/images/image1.jpeg", caption: "Con la funicolare si scende a piazza Cioccaro, 'Ciochee', dove la folla si raccoglie come un'onda viva e il brusio si intreccia alle voci riempiendo l'aria." },
  { image: "/images/image2.jpeg", caption: "Da qui si apre il quartiere custodito nella memoria dei luganesi di un tempo: il Sassello." },
  { image: "/images/image3.jpeg", caption: "Il sole filtra a fatica tra i vicoli così stretti di questo quartiere, sfiorando muri che raccontano più di quanto mostrino." },
  { image: "/images/image4.jpeg", caption: "Un intreccio di viuzze strette e di muri segnati dagli anni. Le case si sfiorano, quasi a sostenersi." },
  { image: "/images/image5.jpeg", caption: "Scorci così sono ormai reliquie di un passato che si ritrae solo nella memoria." },
  { image: "/images/image6.jpeg", caption: "Anche questo angolo è destinato a svanire, cedendo il posto a una nuova via." },
  { image: "/images/image7.jpeg", caption: "I tempi nuovi si annunciano con la moderna funicolare e con l'insegna della ferrovia elettrica Lugano–Tesserete." },
  { image: "/images/image8.jpeg", caption: "La vettura scende verso una città pronta a trasformarsi; di lato, la salita di San Lorenzo resiste, discreta." },
  { image: "/images/image9.jpeg", caption: "Ogni cosa ha il suo tempo. Anche lungo le strade la gente trova uno spazio per fermarsi, per giocare a bocce." },
  { image: "/images/image10.jpeg", caption: "Le arcate in via Pessina si susseguono uguali, scandendo le ore come un antico orologio di pietra." },
  { image: "/images/image11.jpeg", caption: "Lungo la via i buoi trascinano lentamente il carico di botti, passo dopo passo..." },
  { image: "/images/image12.jpeg", caption: "...mentre una donna anziana, curva sotto il peso degli anni e della legna, lascia affiorare un sorriso lieve." },
  { image: "/images/image13.jpeg", caption: "È giorno di mercato in piazza Maraini. Il tempo qui è scandito dal mutare del nome della piazza." },
  { image: "/images/image14.jpeg", caption: "Via Nassa. Già allora centro pulsante di vita, ricca di botteghe e di voci intrecciate." },
  { image: "/images/image15.jpeg", caption: "Un mezzo di trasporto che oggi ci appare curioso, ma un tempo si inseriva con naturalezza nel paesaggio urbano." },
  { image: "/images/image16.jpeg", caption: "I cavalli percorrevano le vie del borgo come presenze familiari, parte stessa della quotidianità cittadina." },
  { image: "/images/image17.jpeg", caption: "Le acque del lago, amate e temute, talvolta superano i loro confini e invadono le strade." },
  { image: "/images/image18.jpeg", caption: "Arrivano lente, insinuandosi tra le pietre, riflettendo facciate e finestre come in uno specchio inquieto." },
  { image: "/images/image19.jpeg", caption: "L'acqua cancella i confini, trasforma le vie in un frammento di lago, sospende il tempo in un'attesa immobile." },
  { image: "/images/image20.jpeg", caption: "Piazza Rizziero Rezzonico, con la fontana Bossi." },
  { image: "/images/image21.jpeg", caption: "Un giorno sarai sommersa, circondata da barche e cigni; poi saranno le automobili a occuparti." },
  { image: "/images/image22.jpeg", caption: "La processione del Corpus Domini entra nell'antica chiesa di Santa Maria degli Angeli." },
  { image: "/images/image23.jpeg", caption: "Le lavandaie riportano alla memoria un lavoro antico e faticoso lungo le rive del lago." },
  { image: "/images/image24.jpeg", caption: "E il panettiere, con la sua bicicletta e il carretto, percorre le vie per rifornire la città." },
  { image: "/images/image25.jpeg", caption: "Così è Lugano: una città che cambia volto senza smettere di portare in sé le tracce di ciò che è stata." },
  { image: "/images/image26.jpeg", caption: "Tra pietre antiche e insegne nuove, tra acqua e asfalto, continua a oscillare tra memoria e futuro." },
  { image: "/images/image27.jpeg", caption: "Tra ombre e botteghe, il vicolo antico sembra trattenere il tempo." },
  { image: "/images/image28.jpeg", caption: "Il mercato vive di voci e frutta colorata, un piccolo teatro quotidiano." },
  { image: "/images/image29.jpeg", caption: "Le arcate mute custodiscono ricordi che sembrano sfiorare l'aria." },
  { image: "/images/image30.jpeg", caption: "Il vicolo vivace dipinge la strada di colori, passi e storie incrociate." },
  { image: "/images/image31.jpeg", caption: "La via si anima tra carri e botteghe: un piccolo mondo che si muove lento sul selciato lucido." },
  { image: "/images/image32.jpeg", caption: "La luce scivola tra i portici mentre la strada sale silenziosa, come trattenendo un respiro antico." },
  { image: "/images/image33.jpeg", caption: "Passi e ombre si intrecciano sotto le arcate eleganti, dove ogni vetrina racconta un frammento di città." },
  { image: "/images/image34.jpeg", caption: "Il lavoro brulica attorno ai calderoni fumanti, e la strada osserva come un teatro affollato." },
  { image: "/images/image35.jpeg", caption: "Sotto i soffitti dipinti del portico, la luce abbraccia la via, rivelando un'eleganza sospesa nel tempo." },
];

// Audio descriptions
const audioData = [
  {
    file: "/audio/ceresio1.mp3",
    title: "La vita sul lago - Parte 1",
    description: "Racconto sulla nascita del turismo balneare a Lugano: i primi bagni pubblici, le terrazze sul lago, le serate danzanti e le tradizioni del carnevale a Capo San Martino."
  },
  {
    file: "/audio/ceresio2.mp3",
    title: "La vita sul lago - Parte 2", 
    description: "La vita operosa sulle rive: pescatori, lavandaie, la lavorazione della canapa, l'allevamento dei bachi da seta e i trasporti tradizionali con le 'ghelle'."
  }
];

// Navigation component
const TimelineNav = ({ activeSection }) => {
  const sections = [
    { id: "hero", label: "Intro", icon: MapPin },
    { id: "past", label: "Passato", icon: Clock },
    { id: "present", label: "Presente", icon: Leaf },
    { id: "future", label: "Futuro", icon: Zap },
    { id: "bibliography", label: "Fonti", icon: BookOpen }
  ];

  return (
    <nav className="timeline-nav" data-testid="timeline-nav">
      {sections.map((section) => (
        <a
          key={section.id}
          href={`#${section.id}`}
          data-testid={`nav-${section.id}`}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
            activeSection === section.id
              ? "bg-[#0A2540] text-white"
              : "text-[#0A2540] hover:bg-[#E3F2FD]"
          }`}
        >
          <section.icon className="w-4 h-4" />
          <span className="hidden md:inline text-sm font-medium">{section.label}</span>
        </a>
      ))}
    </nav>
  );
};

// Audio Player component
const AudioPlayer = ({ audioFile, title, description }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const updateTime = () => setCurrentTime(audio.currentTime);
      const updateDuration = () => setDuration(audio.duration);
      const handleEnded = () => setIsPlaying(false);

      audio.addEventListener('timeupdate', updateTime);
      audio.addEventListener('loadedmetadata', updateDuration);
      audio.addEventListener('ended', handleEnded);

      return () => {
        audio.removeEventListener('timeupdate', updateTime);
        audio.removeEventListener('loadedmetadata', updateDuration);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, []);

  return (
    <div className="card-glass p-6 space-y-4" data-testid="audio-player">
      <audio ref={audioRef} src={audioFile} preload="metadata" />
      <div className="flex items-start gap-4">
        <button
          onClick={togglePlay}
          className="flex-shrink-0 w-14 h-14 rounded-full bg-[#0A2540] text-white flex items-center justify-center hover:bg-[#4A90E2] transition-colors"
          aria-label={isPlaying ? "Pausa" : "Riproduci"}
          data-testid="audio-play-btn"
        >
          {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
        </button>
        <div className="flex-1">
          <h4 className="font-semibold text-[#0A2540] text-lg">{title}</h4>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-500 w-10">{formatTime(currentTime)}</span>
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#4A90E2] transition-all"
            style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
          />
        </div>
        <span className="text-xs text-gray-500 w-10">{formatTime(duration)}</span>
        <Volume2 className="w-4 h-4 text-gray-400" />
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = () => (
  <section 
    id="hero" 
    className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden"
    style={{
      background: "linear-gradient(180deg, #E3F2FD 0%, #FFFFFF 100%)"
    }}
    data-testid="hero-section"
  >
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#4A90E2] rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#0A2540] rounded-full blur-3xl" />
    </div>
    
    <div className="relative z-10 text-center px-6 max-w-4xl mx-auto animate-fade-in-up">
      <h1 className="text-5xl md:text-7xl tracking-tight text-[#0A2540] mb-6">
        Ecomuseo Diffuso
      </h1>
      <p className="text-xl md:text-2xl text-[#4A90E2] font-light italic mb-8">
        Un viaggio attraverso i 3 laghi della regione Insubrica
      </p>
      <div className="max-w-3xl mx-auto text-lg text-gray-700 leading-relaxed space-y-4">
        <p>
          Il percorso lungo i 3 laghi si configura come un ecomuseo diffuso che comprende parte della regione 
          Insubrica, creando così un museo a cielo aperto che si sviluppa attraverso le città di 
          <strong> Lugano, Locarno e Como</strong>.
        </p>
        <p>
          Il progetto accompagna il visitatore in un'esperienza interattiva che unisce storia, arte, 
          letteratura e sensibilizzazione sull'ecosistema lacustre, valorizzando il patrimonio naturale 
          e culturale della regione.
        </p>
      </div>
      
      <div className="flex flex-wrap justify-center gap-4 mt-12">
        <a href="#past" className="btn-primary inline-flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Esplora il Passato
        </a>
        <a href="#present" className="btn-secondary inline-flex items-center gap-2">
          <Leaf className="w-5 h-5" />
          Scopri il Presente
        </a>
        <a href="#future" className="btn-secondary inline-flex items-center gap-2">
          <Zap className="w-5 h-5" />
          Immagina il Futuro
        </a>
      </div>
    </div>
    
    <a 
      href="#past" 
      className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce"
      aria-label="Scorri verso il basso"
    >
      <ChevronDown className="w-8 h-8 text-[#4A90E2]" />
    </a>
  </section>
);

// Past Section
const PastSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  return (
    <section 
      id="past" 
      className="py-20 bg-past"
      data-testid="past-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12 animate-fade-in-up">
          <span className="text-[#4A90E2] text-sm uppercase tracking-widest font-medium">Prima Tappa</span>
          <h2 className="text-4xl md:text-6xl tracking-tight italic text-[#0A2540] mt-2">
            Uno sguardo sul Passato
          </h2>
          <p className="max-w-3xl mx-auto mt-6 text-lg text-gray-700">
            Il visitatore partirà da piazza Cioccaro e seguirà un percorso lungo via Pessina e via Nassa, 
            attraversando quello che un tempo era il quartiere Sassello, fino a giungere a piazza Luini, 
            davanti al LAC. Lungo questo itinerario sarà possibile immergersi nella storia di Lugano.
          </p>
        </div>

        {/* Image Carousel */}
        <div className="mb-16">
          <Carousel 
            className="w-full max-w-5xl mx-auto"
            opts={{ align: "center", loop: true }}
          >
            <CarouselContent>
              {pastCarouselData.map((item, index) => (
                <CarouselItem key={index} className="md:basis-3/4 lg:basis-2/3">
                  <div className="p-2">
                    <Card className="overflow-hidden border-0 shadow-2xl">
                      <CardContent className="p-0 relative aspect-[4/3]">
                        <img
                          src={item.image}
                          alt={`Lugano storica - ${index + 1}`}
                          className="w-full h-full object-cover filter-sepia"
                          data-testid={`carousel-image-${index}`}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
                          <p className="text-white text-sm md:text-base italic leading-relaxed">
                            "{item.caption}"
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2 md:-left-12 bg-white/80 hover:bg-white" />
            <CarouselNext className="right-2 md:-right-12 bg-white/80 hover:bg-white" />
          </Carousel>
        </div>

        {/* Audio Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <h3 className="text-2xl md:text-3xl text-[#0A2540] text-center mb-8">
            Ascolta i racconti del lago
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {audioData.map((audio, index) => (
              <AudioPlayer 
                key={index}
                audioFile={audio.file}
                title={audio.title}
                description={audio.description}
              />
            ))}
          </div>
        </div>

        {/* Historical Stories */}
        <div className="max-w-4xl mx-auto space-y-12">
          <HistoricalStory 
            title="Memorie del Sassello"
            content={`Sassello era un angolo nascosto di Lugano, stretto tra vicoli tortuosi e case ormai stanche, che sembravano sostenersi a vicenda per non crollare sotto il peso del tempo. Chi lo attraversava poteva perdersi in quel labirinto di scalette, portici e passaggi silenziosi, dove ogni muro conservava il respiro di chi vi aveva vissuto.

Le abitazioni erano povere, segnate dall'umidità, dal freddo e dalla mancanza di comodità. Eppure, dietro quelle finestre modeste, batteva una vita intensa. Famiglie intere condividevano spazi ristretti, sacrifici quotidiani e la fatica di arrivare a fine giornata. Ma in mezzo alle difficoltà nasceva qualcosa che altrove sembrava più raro: una solidarietà spontanea, fatta di mani tese, di piatti condivisi e di parole che sapevano consolare.

Poi arrivò il giorno in cui Sassello venne cancellato. Le sue case, i suoi vicoli, le sue voci furono rase al suolo, come se quel pezzo di città non avesse mai avuto un'anima.`}
            source="Agliati, 2016"
          />

          <HistoricalStory 
            title="Via Pessina"
            content={`Passeggiando lungo Via Pessina si percorre una delle strade più antiche e identitarie di Lugano. Un tempo strada caratteristica del "vecchio" centro, collegava idealmente Piazza Cioccaro alle vie interne del quartiere, diventando nei secoli un vivace incrocio di scambi e relazioni.

Il suo stesso nome richiama una vocazione precisa: l'antico toponimo, conosciuto anche nelle varianti Pissina o Pisana, rimanda all'allevamento dei pesci e alla pesca. Per lungo tempo questa zona fu, infatti, sede del mercato cittadino, soprattutto per la vendita di pesce, frutta e ortaggi.

Oggi Via Pessina conserva l'impronta di questa storia stratificata: botteghe, palazzi e scorci raccontano una Lugano laboriosa e mercantile, capace di rinnovarsi nel tempo mantenendo saldo il legame con le proprie radici.`}
            source="Agliati, 2016"
          />

          <HistoricalStory 
            title="Via Nassa"
            content={`Via Nassa oggi elegante e piena di vetrine scintillanti è conosciuta come la strada del lusso internazionale, ma non è sempre stata così: un tempo questa era la contrada di Carona o di San Carlo, una delle strade più antiche di Lugano. In questo borgo si viveva di commercio e artigianato. Il nome "Nassa" viene dalle nasse, gli strumenti per la pesca che venivano appesi nei vicoli quando, un tempo, il lago era ancora il cuore dell'economia locale.

Sotto i portici passeggiava la borghesia: signori eleganti e dame agghindate di gioielli e acconciature vistose. Bastava però attraversare la strada per entrare in un mondo diverso: case popolari, vicoli stretti e odori forti. Via Nassa era un confine invisibile tra due realtà molto diverse.

Via Nassa non è solo una strada di negozi, è un frammento di storia della città di Lugano, un luogo dove il Medioevo, l'Ottocento e la Belle Époque si sono incontrati lasciando tracce che, se sappiamo con quali occhi guardarle, sono ancora visibili.`}
            source="Agliati, 2016"
          />

          <HistoricalStory 
            title="Piazza Cioccaro"
            content={`Piazza Cioccaro è il luogo dove ancora oggi parte la funicolare che sale verso la stazione ferroviaria. Insieme a Piazza della Riforma e a Piazza Dante Alighieri, forma uno dei tre cuori pulsanti del nucleo storico della città.

Dopo il 1844 fu chiamata Piazza Asilo, perché proprio qui sorgeva il primo Asilo Ciani. Con l'inaugurazione della funicolare, nel 1886, la piazza assunse il nome di Piazza Funicolare, finché nel 1942 recuperò l'antico toponimo di Cioccaro, già attestato nei documenti medievali.

A nord, la contrada del Sassello sbocciava proprio in questa piazza, allora uno slargo irregolare ai piedi della cattedrale. Era un punto di incontro fra mondi opposti: da una parte i turisti eleganti che scendevano dalla stazione diretti ai grandi alberghi del lungolago; dall'altra la gente del Sassello.`}
            source="Agliati, 2016; guida turistica Gitti, 2026"
          />
        </div>
      </div>
    </section>
  );
};

// Historical Story component
const HistoricalStory = ({ title, content, source }) => (
  <div className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-[#4A90E2]">
    <h3 className="text-2xl md:text-3xl text-[#0A2540] mb-4">{title}</h3>
    <div className="prose prose-lg text-gray-700 leading-relaxed whitespace-pre-line">
      {content}
    </div>
    <p className="text-sm text-gray-500 mt-4 italic">Fonte: {source}</p>
  </div>
);

// Present Section
const PresentSection = () => (
  <section 
    id="present" 
    className="py-20 bg-present"
    data-testid="present-section"
  >
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-16 animate-fade-in-up">
        <span className="text-[#4A90E2] text-sm uppercase tracking-widest font-medium">Seconda Tappa</span>
        <h2 className="text-4xl md:text-6xl tracking-tight italic text-[#0A2540] mt-2">
          Atelier delle Forme
        </h2>
        <p className="max-w-3xl mx-auto mt-6 text-lg text-gray-700">
          Il presente dell'ecomuseo si concentra su due progetti innovativi che uniscono arte, 
          sostenibilità e tecnologia per sensibilizzare sulla tutela dell'ambiente lacustre.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* 3D Printing Project */}
        <Card className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-shadow duration-300">
          <div className="aspect-video relative overflow-hidden">
            <img 
              src="/images/image44.jpeg" 
              alt="Progetto Stampa 3D"
              className="w-full h-full object-cover image-hover-scale"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/80 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <h3 className="text-2xl text-white font-bold">Postazione Stampa 3D</h3>
            </div>
          </div>
          <CardContent className="p-6 space-y-4">
            <p className="text-gray-700 leading-relaxed">
              L'idea consiste nell'acquisto di una stampante 3D di grandi dimensioni per trasformare 
              parte dei rifiuti plastici raccolti dal lago in opere d'arte. Per la raccolta delle 
              plastiche viene utilizzata una rete "manta" con maglie da 0,3 mm, capace di intercettare 
              sia frammenti visibili sia microplastiche.
            </p>
            <p className="text-gray-700 leading-relaxed">
              I materiali individuati appartengono principalmente a polietilene (PE), polipropilene (PP) 
              e polistirene (PS). La stampante sarà a disposizione della popolazione per lo sviluppo 
              di opere creative attraverso una piattaforma online dedicata.
            </p>
            <div className="pt-4">
              <h4 className="font-semibold text-[#0A2540] mb-2">Progetti simili nel mondo:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Print Your City - Amsterdam</li>
                <li>• Monumento stampato in 3D da rifiuti urbani - Firenze</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Cracking Art Project */}
        <Card className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-shadow duration-300">
          <div className="aspect-video relative overflow-hidden">
            <img 
              src="/images/image45.jpeg" 
              alt="Cracking Art"
              className="w-full h-full object-cover image-hover-scale"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/80 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <h3 className="text-2xl text-white font-bold">Cracking Art</h3>
            </div>
          </div>
          <CardContent className="p-6 space-y-4">
            <p className="text-gray-700 leading-relaxed">
              <em>Cracking</em>, dal verbo inglese "to crack", significa incrinarsi, spezzarsi, rompersi. 
              Cracking catalitico è la reazione chimica con cui il petrolio viene trasformato in plastica, 
              il momento in cui il naturale diviene artificiale.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Invadendo il tessuto urbano, il movimento di Cracking Art si pone l'obiettivo di 
              sensibilizzare l'uomo sulla sua cultura di massa sempre più artificiale, che gradualmente 
              sta perdendo il contatto col naturale e i suoi valori coesivi.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Attraverso la modellazione digitale e la produzione additiva, è possibile progettare 
              animali e forme simboliche partendo da plastica riciclata, mantenendo il carattere iconico 
              e colorato delle installazioni urbane.
            </p>
            <div className="pt-4">
              <a 
                href="https://www.crackingart.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2 text-sm"
                data-testid="cracking-art-link"
              >
                Visita Cracking Art
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Future expansion note */}
      <div className="mt-12 text-center">
        <div className="card-glass max-w-2xl mx-auto">
          <p className="text-gray-700 italic">
            In futuro vogliamo espandere questa idea, non costruendo solo animali, ma anche pezzi 
            unici utili come panchine, torri idroponiche, ed altro ancora.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Future Section
const FutureSection = () => (
  <section 
    id="future" 
    className="py-20 bg-future"
    data-testid="future-section"
  >
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-16 animate-fade-in-up">
        <span className="text-[#FF6B35] text-sm uppercase tracking-widest font-medium font-technical">Terza Tappa</span>
        <h2 className="text-4xl md:text-6xl tracking-tight italic text-[#0A2540] mt-2">
          Cuore di Energia
        </h2>
        <p className="max-w-3xl mx-auto mt-6 text-lg text-gray-700">
          Questa tappa del progetto nasce come un'infrastruttura autosufficiente, realizzata con 
          materiali riutilizzati e sostenibili. Sorgerà sulle rive del Lago Ceresio, in un luogo 
          pensato come spazio d'incontro tra comunità e natura.
        </p>
      </div>

      {/* Structure Overview */}
      <div className="grid lg:grid-cols-2 gap-12 mb-16">
        <div className="space-y-6">
          <h3 className="text-2xl md:text-3xl text-[#0A2540]">La Struttura</h3>
          <p className="text-gray-700 leading-relaxed">
            Un passaggio conduce dalla riva a una piazza circolare costruita in legno, rivestito 
            con piattaforme in grado di convertire in energia utile i passi di coloro che le calpestano, 
            come già visto in un progetto giapponese.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Al centro troviamo un albero che affonda le sue radici nel fondale del lago senza 
            disturbare l'habitat degli animali acquatici, in quanto pianta autoctona. Questo albero 
            può accogliere uccelli migratori, facilitando il loro percorso, e contribuisce ad avere 
            un'atmosfera pulita.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Sulla piazza sono stati aggiunti elementi che contribuiscono all'incontro della comunità: 
            panchine, postazioni per ricaricare lo smartphone e lampioni ricaricati e accesi grazie 
            all'energia della piazza.
          </p>
        </div>
        <div className="relative">
          <img 
            src="/images/struttura_futuro.jpeg" 
            alt="Struttura Cuore di Energia - Parco Ciani"
            className="rounded-xl shadow-2xl w-full"
          />
        </div>
      </div>

      {/* Materials Grid */}
      <div className="mb-16">
        <h3 className="text-2xl md:text-3xl text-[#0A2540] text-center mb-8">Materiali</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MaterialCard 
            title="Piattaforme Pavegen"
            description="Tecnologia che converte l'energia cinetica dei passi in elettricità, unendo innovazione e sostenibilità."
            links={[
              { label: "Pavegen", url: "https://www.pavegen.com/" },
              { label: "Video 1", url: "https://www.youtube.com/watch?v=N_Li7gQQLLY" },
              { label: "Video 2", url: "https://www.youtube.com/watch?v=VD15-2Uriyc" }
            ]}
          />
          <MaterialCard 
            title="Fondamenta in Rovere"
            description="Legno noto per la sua durabilità naturale e la resistenza a funghi e muffe anche a contatto con l'acqua."
            links={[
              { label: "BioResources", url: "https://bioresources.cnr.ncsu.edu/resources/durability-and-protection-of-timber-structures-in-marine-environments-in-europe-an-overview/" },
              { label: "ScienceDirect", url: "https://www.sciencedirect.com/science/article/abs/pii/S1385110116300740" }
            ]}
          />
          <MaterialCard 
            title="Panchine Arancioni"
            icon={<div className="w-8 h-8 rounded-full bg-[#FF6B35]" />}
            description="Simbolo dell'impegno contro la violenza di genere, già presente sul lungolago di Locarno."
          />
          <MaterialCard 
            title="Balaustra in Vetro"
            description="Vetro riutilizzato recuperato dagli scarti di una vetreria ticinese. Trasparente e leggera, protegge senza interrompere la vista."
          />
        </div>
      </div>

      {/* Energy Note */}
      <div className="card-glass max-w-3xl mx-auto text-center">
        <p className="text-gray-700 leading-relaxed">
          La quantità di energia convertita dipende dall'affluenza dell'installazione, perciò non 
          possiamo garantire che sia sempre abbondante; ciò, però, non è un problema, visto che il 
          nostro progetto vuole <strong>sensibilizzare il pubblico alla difficoltà nell'ottenere energia</strong>.
        </p>
      </div>
    </div>
  </section>
);

// Material Card component
const MaterialCard = ({ title, description, links, icon }) => (
  <div className="bg-white rounded-xl p-6 shadow-lg border-t-4 border-[#4A90E2] h-full flex flex-col">
    <div className="flex items-center gap-3 mb-4">
      {icon || <Zap className="w-6 h-6 text-[#4A90E2]" />}
      <h4 className="font-technical font-medium text-[#0A2540]">{title}</h4>
    </div>
    <p className="text-sm text-gray-600 flex-1">{description}</p>
    {links && links.length > 0 && (
      <div className="mt-4 flex flex-wrap gap-2">
        {links.map((link, i) => (
          <a
            key={i}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#4A90E2] hover:text-[#0A2540] inline-flex items-center gap-1 transition-colors"
            data-testid={`material-link-${i}`}
          >
            {link.label}
            <ExternalLink className="w-3 h-3" />
          </a>
        ))}
      </div>
    )}
  </div>
);

// Bibliography Section
const BibliographySection = () => (
  <section 
    id="bibliography" 
    className="py-20"
    style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #0A2540 100%)" }}
    data-testid="bibliography-section"
  >
    <div className="max-w-4xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-4xl md:text-5xl tracking-tight text-[#0A2540]">
          Bibliografia e Fonti
        </h2>
      </div>

      <div className="space-y-8">
        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h3 className="text-xl font-semibold text-[#0A2540] mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Bibliografia dei Testi
          </h3>
          <ul className="space-y-3 text-gray-700">
            <li className="border-l-2 border-[#4A90E2] pl-4">
              A cura di Agliati, C. (2016). <em>Sassello, il quartiere frainteso</em>. Cantonetto
            </li>
            <li className="border-l-2 border-[#4A90E2] pl-4">
              Agliati, M., Taddei, M., & Somazzi, R. (1982). <em>Lugano d'oro 1900</em>. Fototecnica M. Taddei di Lugano.
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h3 className="text-xl font-semibold text-[#0A2540] mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Bibliografia e Sitografia delle Immagini
          </h3>
          <ul className="space-y-3 text-gray-700">
            <li className="border-l-2 border-[#4A90E2] pl-4">
              A cura di Agliati, C. (2016). <em>Sassello, il quartiere frainteso</em>. Cantonetto
            </li>
            <li className="border-l-2 border-[#4A90E2] pl-4">
              Agliati, M., Taddei, M., & Somazzi, R. (1982). <em>Lugano d'oro 1900</em>. Fototecnica M. Taddei di Lugano.
            </li>
            <li className="border-l-2 border-[#4A90E2] pl-4">
              Lugano Cultura. (n.d.). Patrimonio Culturale. 
              <a 
                href="https://patrimonio.luganocultura.ch/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#4A90E2] hover:underline ml-1"
              >
                patrimonio.luganocultura.ch
              </a>
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h3 className="text-xl font-semibold text-[#0A2540] mb-4 flex items-center gap-2">
            <ExternalLink className="w-5 h-5" />
            Link e Risorse Esterne
          </h3>
          <ul className="space-y-3 text-gray-700">
            <li className="border-l-2 border-[#FF6B35] pl-4">
              Cracking Art: 
              <a 
                href="https://www.crackingart.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#4A90E2] hover:underline ml-1"
              >
                crackingart.com
              </a>
            </li>
            <li className="border-l-2 border-[#FF6B35] pl-4">
              Pavegen Energy: 
              <a 
                href="https://www.pavegen.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#4A90E2] hover:underline ml-1"
              >
                pavegen.com
              </a>
            </li>
            <li className="border-l-2 border-[#FF6B35] pl-4">
              BioResources - Timber Durability: 
              <a 
                href="https://bioresources.cnr.ncsu.edu/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#4A90E2] hover:underline ml-1"
              >
                bioresources.cnr.ncsu.edu
              </a>
            </li>
            <li className="border-l-2 border-[#FF6B35] pl-4">
              Structure Magazine - Timber Foundations: 
              <a 
                href="https://www.structuremag.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#4A90E2] hover:underline ml-1"
              >
                structuremag.org
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-16 text-center text-white/80">
        <Separator className="mb-8 bg-white/20" />
        <p className="text-sm">
          Ecomuseo Diffuso - Un progetto per la valorizzazione del territorio dei 3 laghi
        </p>
        <p className="text-xs mt-2 opacity-60">
          Lugano • Locarno • Como
        </p>
      </div>
    </div>
  </section>
);

// Main App component
function App() {
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["hero", "past", "present", "future", "bibliography"];
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App" data-testid="ecomuseo-app">
      <TimelineNav activeSection={activeSection} />
      <main>
        <HeroSection />
        <PastSection />
        <PresentSection />
        <FutureSection />
        <BibliographySection />
      </main>
    </div>
  );
}

export default App;
